import React from 'react';
import { motion } from 'framer-motion';

interface IndustryCategory {
  id: number;
  title: string;
  description: string;
  slug: string;
  iconSvg: React.ReactNode; 
  links: {
    text: string;
    url: string;
  }[];
}

// TradyFrog Themes ke custom icons
const ElectricIcon = (
  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const HomeIcon = (
  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const HealthIcon = (
  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
  </svg>
);

const industryCategories: IndustryCategory[] = [
  {
    id: 1,
    title: "Electric Supplier",
    description: "Industrial switches, wires, and power solutions for your business.",
    slug: "all-categories",
    iconSvg: ElectricIcon,
    links: [
      { text: "LED Lights", url: "/categories/5" },
      { text: "Split Air Conditioner", url: "/categories/15" },
      { text: "Room Heaters", url: "/categories/174" },
      { text: "Drill Machine", url: "/categories/205" },
    ],
  },
  {
    id: 2,
    title: "Home Supplier",
    description: "Premium home appliances and cooling systems for bulk orders.",
    slug: "all-categories",
    iconSvg: HomeIcon,
    links: [
      { text: "Room Cooler", url: "/categories/8" },
      { text: "Ultrasound machines", url: "/categories/66" },
      { text: "Electric Kettle", url: "/categories/175" },
      { text: "Cooler Pump", url: "/categories/72" },
    ],
  },
  {
    id: 3,
    title: "Hospital Supplies",
    description: "Medical grade equipment and safety gear for healthcare facilities.",
    slug: "all-categories",
    iconSvg: HealthIcon,
    links: [
      { text: "Pulse Oximeters", url: "/categories/64" },
      { text: "Bedside Lockers", url: "/categories/62" },
      { text: "Syringes & Needles", url: "/categories/69" },
      { text: "Meditation Accessories", url: "/categories/184" },
    ],
  },
];

const PromoCategoryBanners: React.FC = () => {
  return (
    <div className="mt-10 mb-10 max-w-8xl mx-auto px-4 sm:px-6 lg:px-8"> 
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {industryCategories.map((category) => (
          <motion.div 
            key={category.id} 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white p-6 shadow-lg border border-gray-200 
                       transition-all duration-300
                       rounded-tl-3xl rounded-br-3xl 
                       hover:shadow-2xl hover:-translate-y-2 
                       hover:border-[#00684a] hover:bg-emerald-50"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex flex-col pr-4"> 
                <a
                  href={`${category.slug}`}
                  className="text-xl font-bold text-[#00684a] hover:text-[#004d37] cursor-pointer flex items-center mb-1 transition-colors"
                >
                  {category.title}
                  <span className="ml-2 text-sm">↗</span> 
                </a>
                <p className="text-sm text-gray-600 font-medium">{category.description}</p>
              </div>

              {/* Icon Container with TradyFrog Branding */}
              <div className="w-14 h-14 bg-gradient-to-br from-[#00684a] to-[#0f6f5c] flex items-center justify-center rounded-xl shadow-lg flex-shrink-0">
                {category.iconSvg} 
              </div>
            </div>
            
            {/* Sidebar Products Links Mapping */}
            <div className="flex flex-wrap text-sm mt-6 pt-4 border-t border-gray-100">
              {category.links.map((link, index) => (
                <React.Fragment key={link.text}>
                  <a
                    href={link.url}
                    className="font-bold text-blue-600 hover:text-blue-800 transition-colors py-1"
                  >
                    {link.text}
                  </a>
                  {index < category.links.length - 1 && (
                    <span className="text-gray-300 mx-2 flex items-center">|</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default PromoCategoryBanners;